import {createSelector} from 'reselect';
import {defaultSmtpIntegration} from '../../components/blocks/Email/constants';

import {
  ICreatedIntegrationAction,
  IDeletedIntegrationAction,
  IntegrationActions,
  IntegrationActionTypes,
  IUpdatedIntegrationAction,
} from '../actions/integrationActions';
import {ApiParams, ApiStatus, Integration, INTEGRATION_TYPE, SharingSettingsTypes,} from '../../models';
import {IState} from './index';
import {ILoadedSharingSettingsAction, SharingSettingsActionsTypes,} from '../actions/sharingSettingsActions';

export const initialIntegrationState: IIntegrationState = {
  loadingStatus: null,
  creatingStatus: null,
  updatingStatus: null,
  deletingStatus: null,
  integrations: null,
  integrationsParams: {},
  integration: null, // current integration
  canLoadMore: false,
  numberOfIntegrations: 0,
  isTesting: false,
}

export interface IIntegrationState {
  loadingStatus: ApiStatus | null;
  creatingStatus: ApiStatus | null,
  updatingStatus: ApiStatus | null;
  deletingStatus: ApiStatus | null;
  integrations: Integration[] | null;
  integrationsParams: ApiParams | undefined;
  integration: Integration | null; // current integration
  canLoadMore: boolean;
  numberOfIntegrations: number;
  isTesting: boolean;
}

export default function integrationReducer(
  state: IIntegrationState = initialIntegrationState,
  action: IntegrationActions | ILoadedSharingSettingsAction
): IIntegrationState {
  switch (action.type) {
    case IntegrationActionTypes.LOAD_INTEGRATION:
    case IntegrationActionTypes.LOAD_INTEGRATIONS:
      return {
        ...state,
        loadingStatus: ApiStatus.LOADING,
      }
    case IntegrationActionTypes.TEST_INTEGRATION_REQUEST:
      return {
        ...state,
        isTesting: true,
      }
    case IntegrationActionTypes.LOADING_INTEGRATION_FAILED:
    case IntegrationActionTypes.LOADING_INTEGRATIONS_FAILED:
      return {
        ...state,
        loadingStatus: ApiStatus.FAILED,
      }
    case IntegrationActionTypes.TEST_INTEGRATION_SUCCESS:
    case IntegrationActionTypes.TEST_INTEGRATION_FAILURE:
      return {
        ...state,
        isTesting: false,
      }
    case IntegrationActionTypes.LOADED_INTEGRATION:
      return {
        ...state,
        loadingStatus: ApiStatus.LOADED,
        integration: action.payload,
      }
    case IntegrationActionTypes.LOADED_INTEGRATIONS:
      const newIntegrations = action.payload.result || [];
      const reload = action.payload.reload || false;
      const page_size = action.payload.page_size || 0;

      return {
        ...state,
        loadingStatus: ApiStatus.LOADED,
        integrations: reload ? [...newIntegrations] : [
          ...state.integrations || [],
          ...newIntegrations,
        ],
        canLoadMore: newIntegrations.length === page_size,
        numberOfIntegrations: state.numberOfIntegrations + newIntegrations.length,
      }
    case IntegrationActionTypes.UNSET_INTEGRATION:
      return {
        ...state,
        integration: null,
        loadingStatus: null,
        updatingStatus: null,
        creatingStatus: null,
      };
    case IntegrationActionTypes.UNSET_INTEGRATIONS:
      return {
        ...state,
        integrations: null,
        canLoadMore: false,
        numberOfIntegrations: 0,
        loadingStatus: null,
      }

    case SharingSettingsActionsTypes.LOADED_SHARING_SETTINGS:
      return handleLoadedSharingSettings(state, action);

    case IntegrationActionTypes.CREATE_INTEGRATION:
      return {
        ...state,
        creatingStatus: ApiStatus.LOADING,
      }
    case IntegrationActionTypes.CREATED_INTEGRATION:
      return handleCreateIntegration(state, action);
    case IntegrationActionTypes.CREATING_INTEGRATION_FAILED:
      return {
        ...state,
        creatingStatus: ApiStatus.FAILED,
      }

    case IntegrationActionTypes.UPDATE_INTEGRATION:
      return {
        ...state,
        updatingStatus: ApiStatus.LOADING,
      }
    case IntegrationActionTypes.UPDATING_INTEGRATION_FAILED:
      return {
        ...state,
        updatingStatus: ApiStatus.FAILED,
      }
    case IntegrationActionTypes.UPDATED_INTEGRATION:
      return handleUpdateIntegration(state, action);
    case IntegrationActionTypes.DELETE_INTEGRATION:
      return {
        ...state,
        deletingStatus: ApiStatus.LOADING,
      }
    case IntegrationActionTypes.DELETING_INTEGRATION_FAILED:
      return {
        ...state,
        deletingStatus: ApiStatus.FAILED,
      }
    case IntegrationActionTypes.DELETED_INTEGRATION:
      return handleDeleteIntegration(state, action);
    default:
      return state;
  }
}

// Functions

function handleCreateIntegration(state: IIntegrationState, action: ICreatedIntegrationAction) {
  return {
    ...state,
    integrations: [
      ...state.integrations || [],
      action.payload.result,
    ],
    creatingStatus: ApiStatus.LOADED,
    numberOfIntegrations: state.numberOfIntegrations + 1,
  }
}

function handleUpdateIntegration(state: IIntegrationState, action: IUpdatedIntegrationAction) {
  return {
    ...state,
    updatingStatus: ApiStatus.LOADED,
    integrations: state.integrations?.map(integration => {
      if (integration.uid === action.payload.uid) {
        return action.payload;
      }
      return integration;
    }) || null,
  }
}

function handleDeleteIntegration(state: IIntegrationState, action: IDeletedIntegrationAction) {
  return {
    ...state,
    integrations: state.integrations?.filter(integration => integration.uid !== action.payload) || [],
    numberOfIntegrations: state.numberOfIntegrations - 1,
    deletingStatus: ApiStatus.LOADED,
  }
}

function handleLoadedSharingSettings(
  state: IIntegrationState,
  action: ILoadedSharingSettingsAction
): IIntegrationState {
  if (action.payload.type !== SharingSettingsTypes.INTEGRATION) {
    return state;
  }

  return {
    ...state,
    integrations: state.integrations?.map((integration: Integration) => {
      if (integration.uid === action.payload.uid) {
        return {
          ...integration,
          sharing_settings: action.payload.settings
        }
      }

      return integration;
    }) || state.integrations,
  };
}

// Selectors

export const integrationsSelector = (type: string) => createSelector(
  (state: IState) => state.integration.integrations,
  (integrations: Integration[] | null): Integration[] | null => {
    let filteredIntegrations = integrations?.filter(
      (integration: Integration) => integration?.type === type) || [];
    if (type === 'smtp') {
      // add default smtp integration to the top of the list
      filteredIntegrations = [defaultSmtpIntegration, ...filteredIntegrations];
    }
    return filteredIntegrations;
  },
);

export const tableauSelector = integrationsSelector(INTEGRATION_TYPE.TABLEAU);

export const slackSelector = integrationsSelector(INTEGRATION_TYPE.SLACK);

export const customAppSelector = integrationsSelector(INTEGRATION_TYPE.CUSTOM_APP);

export const openAISelector = integrationsSelector(INTEGRATION_TYPE.OPEN_AI);

export const SftpSelector = integrationsSelector(INTEGRATION_TYPE.SFTP);

export const awsS3Selector = integrationsSelector(INTEGRATION_TYPE.AWS_S3);

export const smtpSelector = integrationsSelector(INTEGRATION_TYPE.SMTP);
